import { useMemo } from 'react';
import ActionLink from '../../../../Components/Dashboard/ActionLink';
import Card from '../../../../Components/Dashboard/Card';
import { CardError, CardLoading } from '../../../../Components/Dashboard/CardState';
import { buildRhythm } from './rhythmModel';
import styles from '../dashboardCards.module.css';

const TITLE = 'Training rhythm';
const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const FULL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TrainingRhythmCard = ({ workouts, now, performancePath }) => {
    const { status, data, error, reload } = workouts;
    const view = useMemo(() => (data ? buildRhythm(data, now) : null), [data, now]);

    let body;
    if (status === 'loading' && !data) body = <CardLoading label={TITLE} />;
    else if (status === 'error') body = <CardError label={TITLE} status={error?.response?.status} onRetry={reload} />;
    else if (view)
        body = (
            <>
                <div className={styles.tl} role="table" aria-label="Sessions per day for four weeks">
                    <div className={styles.tlRow} role="row">
                        <span className={`${styles.th} ${styles.thL}`} role="columnheader">Week of</span>
                        {DOW.map((d, i) => (
                            <span key={i} className={styles.th} role="columnheader">
                                {d}
                                <span className={styles.sr}>{FULL[i].slice(1)}</span>
                            </span>
                        ))}
                        <span className={styles.th} role="columnheader">Done / assigned</span>
                    </div>
                    {view.weeks.map(week => (
                        <div className={styles.tlRow} role="row" key={week.label}>
                            <span className={styles.wk} role="rowheader">
                                {week.label}
                                {week.isCurrent && <span className={styles.sr}> (this week)</span>}
                            </span>
                            {week.days.map(day => (
                                <span
                                    key={day.key}
                                    role="cell"
                                    data-marker={day.marker}
                                    className={`${styles.day} ${day.isToday ? styles.today : ''}`}
                                >
                                    <i className={`${styles.mark} ${styles[day.marker]}`} aria-hidden="true" />
                                    <span className={styles.sr}>{day.label}</span>
                                </span>
                            ))}
                            <span className={styles.sum} role="cell">
                                {week.done} / {week.assigned}
                                <small>{` · ${week.doneKm.toFixed(1)} km`}</small>
                            </span>
                        </div>
                    ))}
                </div>
                <div className={styles.legend} style={{ marginTop: 10 }}>
                    <span><i className={`${styles.mark} ${styles.c}`} />Completed ({view.counts.c})</span>
                    <span><i className={`${styles.mark} ${styles.a}`} />Assigned ({view.counts.a})</span>
                    <span><i className={`${styles.mark} ${styles.d}`} />Declined ({view.counts.d})</span>
                    <span>Rest, no session ({view.counts.r})</span>
                </div>
                <div className={styles.cmp}>
                    <span className={styles.cmpLab}>Last 7 days · planned vs completed</span>
                    <p style={{ margin: 0 }}>{view.current}</p>
                    <p style={{ margin: 0 }}>{view.previous}</p>
                </div>
                <p className={styles.foot}>
                    Fitness, fatigue and form (ATL / CTL / TSB) and ACWR charts stay on the full Performance page.
                </p>
            </>
        );

    return (
        <Card
            id="tr"
            module="athlete-rhythm"
            title={TITLE}
            sub="sessions by day, 4 weeks"
            busy={status === 'loading'}
            right={<ActionLink to={performancePath}>Open Performance</ActionLink>}
        >
            {body}
        </Card>
    );
};

export default TrainingRhythmCard;
